"use client";

import { FC, useCallback, useEffect, useState } from "react";
import cx from "classnames";
import styles from "./popular-features.module.scss";
import BackupCamera from "../../icons/popular-features/backup-camera";
import BlindSpot from "../../icons/popular-features/blind-spot";
import SunRoof from "../../icons/popular-features/sun-roof";
import LeatherSeats from "../../icons/popular-features/leather-seats";
import HeatedSeats from "../../icons/popular-features/heated-seats";
import ThirdRowSeating from "../../icons/popular-features/third-row-seating";
import NavigationSystem from "../../icons/popular-features/navigation-system";
import Bluetooth from "../../icons/popular-features/bluetooth";
import RemoteStart from "../../icons/popular-features/remote-start";
import AppleCarPlay from "../../icons/popular-features/apple-car-play";
import AndroidAuto from "../../icons/popular-features/android-auto";
import { IVDPBlockData, IVDPPageBlock, VDPBlockPosition } from "../../../types/vdp";
import { getTranslationModule } from "../../../utils/translations";
import VDPSection from "@/ui/page-components/vehicle-details-page/vdp-section";
import VDPButton from "@/ui/page-components/vehicle-details-page/button";

interface IPopularFeature {
  name: string;
  icon: JSX.Element;
}

const popularFeaturesList: IPopularFeature[] = [
  {
    name: "backup_camera",
    icon: <BackupCamera />,
  },
  {
    name: "blind_spot_monitoring",
    icon: <BlindSpot />,
  },
  {
    name: "sunroof",
    icon: <SunRoof />,
  },
  {
    name: "leather_seats",
    icon: <LeatherSeats />,
  },
  {
    name: "heated_seats",
    icon: <HeatedSeats />,
  },
  {
    name: "third_row_seating",
    icon: <ThirdRowSeating />,
  },
  {
    name: "navigation_system",
    icon: <NavigationSystem />,
  },
  {
    name: "bluetooth",
    icon: <Bluetooth />,
  },
  {
    name: "remote_start",
    icon: <RemoteStart />,
  },
  {
    name: "apple_carplay",
    icon: <AppleCarPlay />,
  },
  {
    name: "android_auto",
    icon: <AndroidAuto />,
  },
];

const SHOW_FEATURES_NUMBER = 8;
interface IPopularFeatureFull {
  title: string;
  name: string;
  icon: JSX.Element;
}

const PopularFeatures: FC<IVDPPageBlock> = ({ vdpGlobalData, translations, pageContext }) => {
  const t = getTranslationModule(translations, "vdp");
  const [isOpen, setIsOpen] = useState(false);

  const vehicle = vdpGlobalData.vehicle;
  const popularFeatures: (IPopularFeatureFull | undefined)[] = popularFeaturesList
    .map((popularFeaturesItem) => {
      const popularFeature = vehicle?.popularFeatures.find((feature) => popularFeaturesItem.name === feature.value);
      if (popularFeature) {
        return {
          name: popularFeature.value,
          title: popularFeature.title,
          icon: popularFeaturesItem.icon,
        };
      }
      return undefined;
    })
    .filter((item) => !!item);

  const [itemsAlignStart, setItemsAlignStart] = useState(false);

  const calculateItemsClassName = useCallback(() => {
    const items = document.querySelector(".popularFeaturesItems") as HTMLDivElement;

    if (items?.offsetHeight > 101) {
      setItemsAlignStart(true);
    } else {
      setItemsAlignStart(false);
    }
  }, []);

  useEffect(() => {
    calculateItemsClassName();
  }, [isOpen, calculateItemsClassName]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      calculateItemsClassName();
      window.addEventListener("resize", calculateItemsClassName);
    }
    return () => window.removeEventListener("resize", calculateItemsClassName);
  }, [calculateItemsClassName]);

  const showFeaturesToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  return (
    <VDPSection title={t("popular_features")} subtitle={`${vehicle.make} ${vehicle.model} ${vehicle.trim}`}>
      <div className={styles.container}>
        <div
          className={cx(styles.items, "popularFeaturesItems", {
            [styles.alignLeft]: itemsAlignStart,
          })}
        >
          {popularFeatures
            .slice(0, isOpen ? popularFeatures.length : SHOW_FEATURES_NUMBER)
            .map((item?: IPopularFeatureFull, index?: number) => (
              <div className={styles.item} key={index}>
                <div className={styles.itemIcon}>{item?.icon}</div>
                <span className={styles.itemTitle}>{item?.title}</span>
              </div>
            ))}
        </div>
        {popularFeatures.length > SHOW_FEATURES_NUMBER && (
          <VDPButton
            name={"popularFeatureShowMore"}
            className={styles.button}
            onClick={showFeaturesToggle}
            title={isOpen ? "Show Less Features" : `${popularFeatures.length - SHOW_FEATURES_NUMBER} More Features`}
            isMobile={pageContext.isSmartphone}
          />
        )}
      </div>
    </VDPSection>
  );
};

const VDPBlockData: IVDPBlockData = {
  component: (props: IVDPPageBlock, key: any) => (
    <PopularFeatures
      key={key}
      config={props.config}
      siteData={props.siteData}
      pageContext={props.pageContext}
      renderingPosition={props.renderingPosition}
      vdpGlobalData={props.vdpGlobalData}
      translations={props.translations}
    />
  ),
  position: VDPBlockPosition.LEFT,
};

export default VDPBlockData;
